import Label from '@polestar/component-warehouse-react/components/label/Label';
import Slider from '@polestar/component-warehouse-react/components/slider/Slider';
import styled from '@emotion/styled';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import FormField, { FormFieldTooltipContainer, hasErrorValue, } from '@polestar/component-warehouse-react/components/form-field/FormField';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { overflowEllipsis } from '@polestar/component-warehouse-react/helpers/style';
import { css } from '@emotion/react';
import { useContainerFocusTriggers } from '@polestar/component-warehouse-react/hooks/useContainerFocusTriggers';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import MinimalHitboxTypes from '@polestar/component-warehouse-react/tokens/minimal-hitbox-types/MinimalHitboxTypes';
const SliderField = forwardRef(({ min = 0, max = 100, roundValue = true, value = 0, valueFormat = (value) => `${value}`, inputFormat = (value) => `${value}`, name, onBlur, onFocus, onChange, onInput, steps, disabled, required, label, hint, error, errorMessage, trailing, fieldProps, tooltip, ...props }, ref) => {
    // needed for also updates during dragging where the hidden input value is only needed when drag has ended or clicked or key pressed
    const [labelValue, setLabelValue] = useState(value);
    const memoizedId = useSSRSafeId('slider-field', props.id);
    const memoizedFieldId = `${memoizedId}-container`;
    const hasError = hasErrorValue(error, errorMessage);
    useEffect(() => {
        setLabelValue(value);
    }, [value]);
    const handleChange = useCallback((val) => {
        const roundedValue = roundValue ? Math.round(val) : val;
        onChange === null || onChange === void 0 ? void 0 : onChange(roundedValue);
    }, [onChange, roundValue]);
    const handleInput = useCallback((val) => {
        const roundedValue = roundValue ? Math.round(val) : val;
        setLabelValue(roundedValue);
        onInput === null || onInput === void 0 ? void 0 : onInput(roundedValue);
    }, [onInput, roundValue]);
    const { focused, focusBlurEvents } = useContainerFocusTriggers({ name, onFocus, onBlur });
    return (React.createElement(FormField, { ...fieldProps, id: memoizedFieldId, border: false, disabled: disabled, required: required, forId: memoizedId, hint: hint, error: error, errorMessage: errorMessage, focused: focused, trailing: trailing, isTextInput: false },
        React.createElement("div", { style: { width: '100%' }, ...focusBlurEvents },
            React.createElement(LabelWrapper, null,
                React.createElement(SliderFieldLabel, { id: `${memoizedId}-label`, disabled: disabled || false, error: hasError },
                    label,
                    required && React.createElement("span", { "aria-hidden": true }, "\u00A0*")),
                React.createElement(SliderFieldValue, { tooltip: !!tooltip && tooltip !== '', error: hasError, disabled: disabled || false }, valueFormat(labelValue))),
            React.createElement(StyledSlider, { error: hasError, ref: ref, steps: steps, min: min, max: max, ...props, disabled: disabled, id: memoizedId, "aria-labelledby": `${memoizedId}-label`, value: value, onChange: handleChange, onInput: handleInput, tooltip: !!tooltip && tooltip !== '' }),
            React.createElement("input", { type: "hidden", name: name, value: inputFormat(value) }),
            tooltip && tooltip !== '' && (React.createElement(FormFieldTooltipWrapper, null,
                React.createElement(FormFieldTooltipContainer, { label: tooltip, orientation: Orientations.vertical, minimalHitboxType: MinimalHitboxTypes.both }))))));
});
SliderField.displayName = 'SliderField';
export default SliderField;
const LabelWrapper = styled.div `
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-direction: row;
`;
const StyledSlider = styled(Slider)(({ tooltip }) => css(tooltip &&
    css `
        width: calc(
          100% -
            ${createCustomSpacing(
    /* 10 is the spacing, 16 is the size of the icon */
    26 / 16)});
        );
      `));
const SliderFieldLabel = styled(Label) `
  flex: 1;
  color: ${({ theme, disabled, error }) => disabled
    ? theme.formField.states.disabled.labelColor
    : error
        ? theme.formField.states.error.labelColor
        : theme.formField.states.enabled.labelColor};
  ${overflowEllipsis};
`;
const SliderFieldValue = styled(Label)(({ theme, disabled, error, tooltip }) => css(css `
        color: ${disabled
    ? theme.formField.states.disabled.inputTextColor
    : theme.formField.states.enabled.inputTextColor};
        text-align: end;
        flex: 1;
      `, tooltip &&
    css `
          margin-inline-end: ${createCustomSpacing(
    /* 10 is the spacing, 16 is the size of the icon */
    26 / 16)};
        `, error &&
    css `
          color: ${theme.slider.errorColor};
        `));
const FormFieldTooltipWrapper = styled.div `
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  & div span::before {
    inset-block-start: 30%;
    inset-inline-start: 100%;
  }
  & div {
    inset-inline-end: 0;
    inset-block-start: ${createCustomSpacing(2 / 16)};
  }
`;
